import React, { Component } from 'react';
import { Button, Modal, ModalBody } from 'reactstrap';
import { connect } from 'react-redux';
import { LuRefreshCw } from "react-icons/lu";

import DatePicker from '../../../../components/forms/DatePicker';
import SaleDetailView from '../../../../components/sale/SaleDetailView';
import HeaderComponent from '../../../../components/header/HeaderComponent';
import TableView from '../../../../components/tableView/TableView/TableView';
import ErrorComponent from '../../../../components/error/ErrorComponent';
import LoadingComponent from '../../../../components/loading/LoadingComponent';

const translator = require('../../../../utils/translator/translator');
const dispatchUtility = require('../../../../utils/domain/dispatchUtility');
const businessDateUtil = require('../../../../utils/dateUtil/businessDateUtil');
const dateUtil = require('../../../../utils/dateUtil/dateUtil');
const logger = require('../../../../utils/logger/logger');
const printUtil = require('../../../../utils/print/printUtil');
const viewUtil = require('../../../../utils/view/viewUtil');
const driverSalesReportDataUtils = require('./driverSalesReportDataUtils');
const driverSaleUtil = require('./utils/driverSaleUtil');
class DriverSalesReport extends Component 
{
    constructor(props)
    {
        super(props);
        this.state = this.defaultState;
        this.datePickerContainerRef = React.createRef(null);
    }

    get defaultState()
    {
        return {
            currentDate: null,
            driverSales: null,              // Sales records are domain objects and must not be modified
            selectedDriverId: null,         // selectedDriverId = "" for Unassigned
            selectedSaleDetail: null,
            showDatePicker: false,
            error: null,
            isLoading: false
        }
    }

    safeUpdateState(newState)
    {
        if (this.mounted)
        {
            this.setState(newState);
        }
    }

    componentDidMount() 
    {
        this.mounted = true;
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState, snapshot)
    {
        if (prevProps.todaysSalesUpdatedDateTime !== this.props.todaysSalesUpdatedDateTime) 
        {
            // Implies todaySales updated
            if (businessDateUtil.isDateSameAsTodayBusinessDate(this.state.currentDate))
                this.loadData();
        }
    }

    componentWillUnmount() 
    {
        this.mounted = false
    }

    onRefreshData = () =>
    {
        this.loadData(true);    // forceReloading = true
    }

    onDateChange = (newDate) =>
    {
        if (newDate)
        {
            this.setState({ currentDate: newDate, showDatePicker: false }, () => this.loadData());
        }
    }

    onDatePickerOutsideClick = () =>
    {
        this.setState({ showDatePicker: false })
    }

    onSelectDriverSaleSummary = (driverId) =>
    {
        this.setState({ selectedDriverId: driverId });
    }

    onSelectDriverSaleDetail(saleId)
    {
        this.setState({ selectedSaleDetail: this.state.driverSales.find(sale => sale.id === saleId) });
    }

    onMarkSaleDetailsAsPaid = async (salesDetailList) =>
    {
        const { driverSales } = this.state;

        try
        {
            for (const saleDetailItem of salesDetailList)
            {
                const sale = driverSales.find(sale => sale.id === saleDetailItem.saleId);
                driverSaleUtil.updateSaleAsPaidInFullByCash(sale);
            }

            viewUtil.showSuccessAlert(translator.getLabel("all_sales_marked_as_paid_in_full_by_cash_successfully"));
        }
        catch (error)
        {
            viewUtil.showErrorAlert(`${translator.getLabel("error_occurred_while_updating_sales")},\n${translator.getLabel("error")}: ${error}`);
        }
        finally
        {
            this.setState({});
        }
    }

    onPrint()
    {
        const salesSummary = driverSalesReportDataUtils.getDriverSalesSummary(this.state.driverSales);
        if (salesSummary.length === 0) return;

        const dataTables = salesSummary.map(saleSummary => driverSalesReportDataUtils.getDriverSaleSummaryTableData(saleSummary));

        printUtil.printDriverSaleReport({
            reportTitle: this.props.title,
            reportDate: dateUtil.formatDate(this.state.currentDate, "dddd, MMM DD, yyyy"),
            data: { tableData: dataTables.map(dataTable => dataTable.renderPayload()) }
        });

        viewUtil.showSuccessAlert(translator.getLabel("driver_sales_summary_print_instructions_sent_to_the_printer_successfully"));
    }

    render()
    {
        if (!this.mounted) return null;

        return (
            <>

                <div className='h-100 mt-2 px-2 d-flex flex-column'>

                    <div>{this.renderToolbar()}</div>

                    <div className='flex-grow-1 pt-2 pb-3 overflow-auto'>
                        {this.renderReportBody()}
                    </div>

                </div>

                {/* Modal screens */}

                <DatePicker
                    value={this.state.currentDate}
                    innerRef={this.datePickerContainerRef}
                    isOpen={this.state.showDatePicker}
                    onChange={this.onDateChange}
                    onOutSideClick={this.onDatePickerOutsideClick} />

                {
                    this.state.selectedSaleDetail && this.renderSaleDetailViewDialogue()
                }

            </>
        );
    }

    renderToolbar()
    {
        const { currentDate, isLoading: isDisabled } = this.state;

        const dateCaption =
            businessDateUtil.isDateSameAsTodayBusinessDate(currentDate)
                ? <span className='px-4'>{translator.getLabel("today")}</span>
                : <span>{dateUtil.formatDate(currentDate, "MMM DD, yyyy")}</span>;

        return (
            <HeaderComponent title={this.props.title}>
                <div className='d-flex px-2'>
                    <Button
                        className='ms-auto btn-action px-5'
                        disabled={isDisabled}
                        onClick={() =>
                        {
                            logger.usage("DriverSalesReport.Refresh", "Staff clicked refresh button to refresh sales");
                            this.onRefreshData();
                        }}>
                        <LuRefreshCw />
                    </Button>
                    <Button
                        className='ms-3 btn-action'
                        disabled={isDisabled}
                        innerRef={this.datePickerContainerRef}
                        onClick={() =>
                        {
                            logger.usage("DriverSalesReport.Calendar", "Staff clicked calendar button to change date");
                            this.setState(prev => ({ showDatePicker: !prev.showDatePicker }));
                        }}>
                        {dateCaption}
                    </Button>
                    <Button
                        className='ms-2 btn-action'
                        disabled={isDisabled}
                        onClick={() =>
                        {
                            logger.usage("DriverSalesReport.Print", "Staff clicked print button to print");
                            this.onPrint();
                        }}>
                        {translator.getLabel("print")}
                    </Button>
                    <Button
                        className='ms-3 btn-cancel'
                        onClick={() =>
                        {
                            logger.usage("DriverSalesReport.Exit", "Staff clicked exit button to exit");
                            dispatchUtility.dispatchScreenKeyboardAction(null);
                            dispatchUtility.dispatchNavigationPageAction(null);
                        }}>
                        {translator.getLabel("exit")}
                    </Button>
                </div>
            </HeaderComponent>
        );
    }

    renderReportBody()
    {
        const { isLoading, error, driverSales } = this.state;

        if (isLoading)
        {
            return (
                <div className='h-100 d-flex justify-content-center align-items-center'>
                    <LoadingComponent waitMessage={translator.getLabel("please_wait_loading_data")} />
                </div>
            );
        }

        if (error != null)
        {
            return (
                <div className='h-100 d-flex justify-content-center align-items-center'>
                    <ErrorComponent error={error} />
                </div>
            );
        }

        if (driverSales.length === 0)
        {
            return (
                <div className='h-100 d-flex justify-content-center align-items-center'>
                    <h3>{translator.getLabel("no_driver_sales_data_exists_for_the_selected_date")}</h3>
                </div>
            );
        }

        return this.renderReportData();
    }

    renderReportData()
    {
        return (
            <div className='h-100 d-flex'>
                {
                    this.renderDriverSalesSummaryList()
                }
                {
                    this.renderDriverSalesDetailList()
                }
            </div>
        );
    }

    renderDriverSalesSummaryList()
    {
        const { driverSales } = this.state;

        const salesSummary = driverSalesReportDataUtils.getDriverSalesSummary(driverSales);

        return (
            <div className='w-50 h-100 me-1 d-flex flex-column border rounded-top'>

                {/* Header */}
                <div className='report-card-header mb-2 p-2 fs-5'>Sales Summary</div>

                {/* List */}
                <div className='flex-grow-1 d-flex flex-column overflow-auto'>
                    {
                        salesSummary.map(saleSummary => this.renderDriverSalesSummaryItem(saleSummary))
                    }
                </div>

            </div>
        );
    }

    renderDriverSalesSummaryItem(saleSummary)
    {
        const isSelected = this.state.selectedDriverId === saleSummary.driverId;
        const selectedClassName = isSelected ? 'border-2 border-primary' : '';

        return (
            <div
                key={saleSummary.driverId}
                className={`m-2 p-2 d-flex flex-column border bg-white shadow-sm ${selectedClassName}`} role='button'
                onClick={() => this.setState({ selectedDriverId: saleSummary.driverId })}>
                <TableView tableData={driverSalesReportDataUtils.getDriverSaleSummaryTableData(saleSummary)} />
            </div>
        );
    }

    renderDriverSalesDetailList()
    {
        const { driverSales, selectedDriverId } = this.state;

        const salesDetailList = driverSalesReportDataUtils.getDriverSalesDetail(driverSales, selectedDriverId);
        const outstandingSalesCount = salesDetailList.filter(sale => !sale.isFullyPaid).length;

        return (
            <div className='w-50 h-100 ms-1 d-flex flex-column border rounded-top'>

                {/* Header */}
                <div className='report-card-header mb-2 p-2 d-flex flex-row justify-content-between'>
                    <div className='text-small text-white'><small>{selectedDriverId !== null && driverSalesReportDataUtils.getDriverName(selectedDriverId)}</small></div>
                    <div className='fs-5'>{translator.getLabel("sales_detail")}</div>
                    <div>
                        {
                            salesDetailList.length > 0 &&
                            <Button size='sm' color='warning' className='px-3 py-1'
                                disabled={outstandingSalesCount === 0}
                                onClick={
                                    () =>
                                        viewUtil.showPrompt(
                                            translator.getLabel("mark_sales_as_paid"),
                                            `${translator.getLabel("mark_selected")} ${outstandingSalesCount} ${translator.getLabel("driver_sale_as_fully_paid_by_cash")}`,
                                            translator.getLabel("please_confirm"),
                                            () => this.onMarkSaleDetailsAsPaid(salesDetailList))
                                }>
                                {`${translator.getLabel("mark_paid")} (${outstandingSalesCount})`}
                            </Button>
                        }
                    </div>
                </div>

                {/* List */}
                <div className='flex-grow-1 d-flex flex-column overflow-auto'>
                    {
                        selectedDriverId === null
                            ? <div className='h-100 d-flex justify-content-center align-items-center'>
                                <h4>{translator.getLabel("select_driver_summary_record_to_view_details")}</h4>
                            </div>
                            : salesDetailList.map(saleDetailItem => this.renderDriverSalesDetailItem(saleDetailItem))
                    }
                </div>

            </div>
        );
    }

    renderDriverSalesDetailItem(saleDetailItem)
    {
        return (
            <div
                key={saleDetailItem.saleId}
                className='m-2 p-2 d-flex flex-column border bg-white shadow-sm' role='button'
                onClick={() => this.onSelectDriverSaleDetail(saleDetailItem.saleId)}>
                <TableView tableData={driverSalesReportDataUtils.getDriverSaleDetailTableData(saleDetailItem)} />
            </div>
        );
    }

    renderSaleDetailViewDialogue()
    {
        return (
            <Modal
                isOpen={true} centered animation={false} fade={false}
                style={{ minWidth: '35vw', maxWidth: '35vw' }}
                toggle={() =>
                {
                    this.setState({ selectedSaleDetail: null });
                }} >
                <ModalBody>
                    <div style={{ height: '80vh', overflowY: 'auto' }}>
                        <SaleDetailView sale={this.state.selectedSaleDetail}
                            onDispatch={() =>
                            {
                                this.setState({ selectedSaleDetail: null });
                            }} />
                    </div>
                </ModalBody>
            </Modal>);
    }

    async loadData(forceReloading)
    {
        // forceReloading = true when user refreshes data

        try
        {
            const currentDate = this.state.currentDate || businessDateUtil.getTodayBusinessDate();
            this.setState(Object.assign(this.defaultState, { currentDate: currentDate, isLoading: true }));

            const driverSales = await driverSalesReportDataUtils.loadDriverSalesData(currentDate, forceReloading);

            this.safeUpdateState({ driverSales: driverSales });
        }
        catch (error)
        {
            logger.error(`Error occurred while loading data in DriverSalesReport; Error: ${error}`, {}, error);
            this.safeUpdateState({ error: error })
        }
        finally
        {
            this.safeUpdateState({ isLoading: false })
        }
    }
}

const mapStateToProps = state => 
{
    return { todaysSalesUpdatedDateTime: state.todaysSalesUpdatedDateTime }
}

export default connect(mapStateToProps)(DriverSalesReport)